import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  afterNextRender,
  inject,
} from '@angular/core';

/**
 * KAN-676: "is the CSS line clamp on this element actually hiding text?"
 *
 * Put this on a line-clamped element and `clampOverflowChange` emits `true`
 * when its content is taller than its box, `false` when it fits. A character
 * count cannot answer this: whether 250 characters overflow 6 lines depends on
 * the width, which changes when a phone is rotated. So the answer is measured,
 * and re-measured:
 *
 *   - on every size change, via a ResizeObserver (which also fires once when it
 *     starts observing, giving the first reading);
 *   - after the bound text changes, because new text in a clamped box of the
 *     same height does not resize the box, so the observer alone stays silent.
 *
 * The host decides what to do with the reading (typically show a Show more
 * control). Note the reading goes `false` once the host removes the clamp to
 * expand the text, so a host should ignore it while expanded.
 *
 * The emission arrives outside any template event, so in this zoneless app the
 * host should store it in a signal.
 */
@Directive({
  selector: '[appClampOverflow]',
  standalone: true,
})
export class ClampOverflowDirective implements AfterViewInit, OnChanges, OnDestroy {
  /** The clamped text. Bound so a text change triggers a re-measure. */
  @Input('appClampOverflow') text: string | null | undefined;
  @Output() clampOverflowChange = new EventEmitter<boolean>();

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private observer: ResizeObserver | null = null;

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') {
      this.measure();
      return;
    }
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.el.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['text'] && !changes['text'].firstChange) {
      afterNextRender(() => this.measure(), { injector: this.injector });
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private measure(): void {
    const el = this.el.nativeElement;
    // 1px of slack: line-height rounding can leave scrollHeight a pixel over.
    this.clampOverflowChange.emit(el.scrollHeight > el.clientHeight + 1);
  }
}
